// Pure tax + fee estimate calculator. Zero hardcoded rates.
// Reused by the Preview Calculator now and Parts Assist / quoting later.

export type FeeKind =
  | 'shop_supplies'
  | 'tire_disposal'
  | 'hazmat'
  | 'environmental'
  | 'fluid_disposal'
  | 'core_charge'
  | 'custom';

export type FeeCalcType = 'percent_labor' | 'percent_ticket' | 'flat' | 'per_unit';

export interface FeeRule {
  id?: string;
  kind: FeeKind;
  label: string;
  calc_type: FeeCalcType;
  amount: number;          // % expressed as decimal (e.g. 0.08) or dollar amount / per-unit dollars
  cap_amount?: number | null;
  taxable: boolean;
  enabled: boolean;
}

export interface TaxabilityCategories {
  parts: boolean;
  labor: boolean;
  tires: boolean;
  shop_supplies: boolean;
  disposal: boolean;
  hazmat: boolean;
  core: boolean;
  sublet: boolean;
}

export interface TaxSettingsLike {
  combined_rate: number;
}

export interface ComputeEstimateInput {
  partsSubtotal: number;
  laborSubtotal: number;
  ticketSubtotal?: number;       // optional override; defaults to parts + labor
  tireUnits?: number;             // for per_unit fees (e.g. tire disposal)
  feeRules: FeeRule[];
  taxRules: { categories: TaxabilityCategories };
  taxSettings: TaxSettingsLike;
}

export interface FeeBreakdownLine {
  ruleId?: string;
  kind: FeeKind;
  label: string;
  amount: number;
  taxable: boolean;
}

export interface ComputeEstimateResult {
  feesBreakdown: FeeBreakdownLine[];
  feesTotal: number;
  taxableBase: number;
  tax: number;
  total: number;
}

function computeFeeAmount(rule: FeeRule, ctx: { partsSubtotal: number; laborSubtotal: number; ticketSubtotal: number; tireUnits: number }): number {
  let raw = 0;
  switch (rule.calc_type) {
    case 'percent_labor':
      raw = ctx.laborSubtotal * rule.amount;
      break;
    case 'percent_ticket':
      raw = ctx.ticketSubtotal * rule.amount;
      break;
    case 'flat':
      raw = rule.amount;
      break;
    case 'per_unit':
      raw = rule.amount * ctx.tireUnits;
      break;
  }
  if (rule.cap_amount != null && rule.cap_amount > 0 && raw > rule.cap_amount) {
    raw = rule.cap_amount;
  }
  return Math.max(0, raw);
}

/** Map a fee kind to the taxability category that controls it. */
function feeKindToCategory(kind: FeeKind): keyof TaxabilityCategories | null {
  switch (kind) {
    case 'shop_supplies': return 'shop_supplies';
    case 'tire_disposal':
    case 'fluid_disposal': return 'disposal';
    case 'hazmat':
    case 'environmental': return 'hazmat';
    case 'core_charge': return 'core';
    case 'custom': return null; // honor rule.taxable directly
  }
}

export function computeEstimate(input: ComputeEstimateInput): ComputeEstimateResult {
  const { partsSubtotal, laborSubtotal, feeRules, taxRules, taxSettings } = input;
  const ticketSubtotal = input.ticketSubtotal ?? (partsSubtotal + laborSubtotal);
  const tireUnits = input.tireUnits ?? 0;
  const cats = taxRules.categories;

  const ctx = { partsSubtotal, laborSubtotal, ticketSubtotal, tireUnits };
  const feesBreakdown: FeeBreakdownLine[] = [];

  for (const rule of feeRules) {
    if (!rule.enabled) continue;
    const amount = computeFeeAmount(rule, ctx);
    if (amount <= 0) continue;

    // Effective taxability: rule.taxable AND category is taxable in shop's tax rules
    const cat = feeKindToCategory(rule.kind);
    const categoryTaxable = cat ? cats[cat] : true;
    const effectiveTaxable = rule.taxable && categoryTaxable;

    feesBreakdown.push({
      ruleId: rule.id,
      kind: rule.kind,
      label: rule.label,
      amount: Number(amount.toFixed(2)),
      taxable: effectiveTaxable,
    });
  }

  const feesTotal = feesBreakdown.reduce((s, f) => s + f.amount, 0);

  // Build taxable base from category toggles
  let taxableBase = 0;
  if (cats.parts) taxableBase += partsSubtotal;
  if (cats.labor) taxableBase += laborSubtotal;
  for (const f of feesBreakdown) if (f.taxable) taxableBase += f.amount;

  const rate = taxSettings.combined_rate || 0;
  const tax = Number((taxableBase * rate).toFixed(2));
  const total = Number((partsSubtotal + laborSubtotal + feesTotal + tax).toFixed(2));

  return {
    feesBreakdown,
    feesTotal: Number(feesTotal.toFixed(2)),
    taxableBase: Number(taxableBase.toFixed(2)),
    tax,
    total,
  };
}

// =============================================================================
// PHASE 2 — Internal Parts Line economics
// =============================================================================

/**
 * Pricing matrix row: cost less-than-or-equal `maxCost` → multiplier on cost = suggested sell.
 * Last row should use Infinity as `maxCost` to act as catch-all.
 */
export interface PricingMatrixRow {
  maxCost: number;
  multiplier: number;
}

/**
 * FALLBACK_PRICING_MATRIX
 * -----------------------
 * Suggestion-only. Used by `suggestedSellFromCost` when a shop has no
 * custom pricing matrix configured.
 *
 * Hard rules (Phase 2 + DMC-H):
 *  1. Output is a suggestion, never authoritative shop pricing policy.
 *  2. The Phase 3.5 authority contract — `actual_sell_each` /
 *     `actual_sell_total` plus `manual_price_locked` — is the SOLE
 *     authority for "manual override" in `computePartLineEconomics`. The
 *     pre-Phase-3.5 legacy `unitSell` fallback was retired in DMC-H once
 *     the live audit confirmed zero rows depended on it.
 *  3. UI surfaces that display values derived from this matrix MUST label
 *     them "Suggested" / "Estimate" — never "Final", "Invoice",
 *     "Customer Total", "Shop Price", or "List Price".
 *  4. No customer-facing or invoice-like surface may treat these values as
 *     official pricing. A future shop-level pricing matrix table will be
 *     the only authoritative source.
 *
 * Future contributors: do NOT promote this constant to authoritative
 * pricing. Build a proper shop-configurable matrix table instead.
 */
export const FALLBACK_PRICING_MATRIX: PricingMatrixRow[] = [
  { maxCost: 5,        multiplier: 3.5 },
  { maxCost: 25,       multiplier: 2.5 },
  { maxCost: 100,      multiplier: 1.9 },
  { maxCost: 500,      multiplier: 1.55 },
  { maxCost: 2000,     multiplier: 1.4 },
  { maxCost: Infinity, multiplier: 1.3 },
];

// =============================================================================
// PHASE 3.5 — Canonical OptiBay default matrix
// =============================================================================
// Multiplier is the source of truth. Markup% and GP% are DERIVED.
// Mirrored in DB by `seed_default_pricing_matrix(shop_id)`.
export interface OptiBayMatrixTier { minCost: number; maxCost: number | null; multiplier: number; }
export const OPTIBAY_DEFAULT_MATRIX: OptiBayMatrixTier[] = [
  { minCost: 0.00,    maxCost: 2.00,    multiplier: 9.50 },
  { minCost: 2.01,    maxCost: 10.00,   multiplier: 6.25 },
  { minCost: 10.01,   maxCost: 30.00,   multiplier: 4.75 },
  { minCost: 30.01,   maxCost: 60.00,   multiplier: 3.85 },
  { minCost: 60.01,   maxCost: 120.00,  multiplier: 3.05 },
  { minCost: 120.01,  maxCost: 250.00,  multiplier: 2.55 },
  { minCost: 250.01,  maxCost: 500.00,  multiplier: 2.35 },
  { minCost: 500.01,  maxCost: 2500.00, multiplier: 1.95 },
  { minCost: 2500.01, maxCost: null,    multiplier: 1.85 },
];

/** System hard cap on any multiplier — enforced in code AND in DB CHECK. */
export const SYSTEM_MAX_MULTIPLIER = 10;

export const round2 = (n: number): number => Math.round(n * 100) / 100;

/** Derive markup % from multiplier. (m - 1) * 100 */
export const deriveMarkupPctFromMultiplier = (m: number): number =>
  round2((m - 1) * 100);

/** Derive gross profit % from multiplier. (1 - 1/m) * 100 */
export const deriveGpPctFromMultiplier = (m: number): number =>
  m > 0 ? round2((1 - 1 / m) * 100) : 0;

/** Find the matrix tier for a given cost. Returns null when no tier matches or cost invalid. */
export function findMatrixTier(cost: number, rows: PricingMatrixRow[]): PricingMatrixRow | null {
  if (!cost || cost <= 0 || !Number.isFinite(cost)) return null;
  if (!rows.length) return null;
  for (const r of rows) {
    if (cost <= r.maxCost) return r;
  }
  return rows[rows.length - 1] ?? null;
}

/**
 * Suggest a sell price from cost using the provided matrix.
 * Returns 0 if cost is non-positive. Suggestion-only — see FALLBACK_PRICING_MATRIX guardrails.
 */
export function suggestedSellFromCost(
  cost: number,
  matrix: PricingMatrixRow[] = FALLBACK_PRICING_MATRIX,
): number {
  if (!cost || cost <= 0) return 0;
  for (const row of matrix) {
    if (cost <= row.maxCost) {
      return Number((cost * row.multiplier).toFixed(2));
    }
  }
  // Should be unreachable if matrix has Infinity catch-all; safety fallback:
  return Number((cost * 1.3).toFixed(2));
}

export interface PartLineEconomicsInput {
  qty: number;
  unitCost: number | null | undefined;
  /**
   * Phase 3.5 authority contract — the SOLE manual-sell authority as of
   * DMC-H. When both are null, callers fall through to the suggested
   * price computed from `unitCost` × matrix. Pricing-method-aware:
   * `matrix_grouped` uses `actualSellTotal` divided by `qty`;
   * everything else uses `actualSellEach`.
   */
  actualSellEach?: number | null;
  actualSellTotal?: number | null;
  pricingMethod?: string | null;
  manualPriceLocked?: boolean | null;
  coreCharge?: number | null;
  taxable?: boolean;
  matrix?: PricingMatrixRow[];           // injectable matrix; defaults to fallback
}

export interface PartLineEconomicsResult {
  effectiveSell: number;     // authority-contract value if present, else suggested
  suggestedSell: number;     // always computed for hint display
  isUsingSuggested: boolean; // true when no authority field was set
  lineSubtotal: number;      // qty * effectiveSell
  gpDollars: number;         // qty * (effectiveSell - unitCost)
  gpPercent: number;         // (effectiveSell - unitCost) / effectiveSell * 100
  coreLine: number;          // qty * coreCharge
  taxable: boolean;
}

export function computePartLineEconomics(input: PartLineEconomicsInput): PartLineEconomicsResult {
  const qty = Math.max(0, Number(input.qty || 0));
  const cost = Number(input.unitCost ?? 0);
  const matrix = input.matrix ?? FALLBACK_PRICING_MATRIX;
  const suggested = suggestedSellFromCost(cost, matrix);
  // DMC-H: Phase 3.5 authority contract is the SOLE manual-sell authority.
  // The pre-Phase-3.5 legacy `unitSell` fallback was retired after a live
  // audit confirmed zero parts_lines rows had `unit_sell` populated without
  // a matching `actual_sell_each`/`actual_sell_total`. Phase 3.5 triggers
  // (`parts_lines_normalize_unit_sell` + `parts_lines_enforce_authority`)
  // guarantee every NEW write produces both columns; the legacy-only state
  // can no longer be created.
  const hasAuthority = input.actualSellEach != null || input.actualSellTotal != null;
  const isManualOverride = hasAuthority && !!input.manualPriceLocked;
  let effective: number;
  if (input.pricingMethod === 'matrix_grouped' && input.actualSellTotal != null && qty > 0) {
    effective = round2(Number(input.actualSellTotal) / qty);
  } else if (input.actualSellEach != null) {
    effective = Number(input.actualSellEach);
  } else if (input.actualSellTotal != null && qty > 0) {
    effective = round2(Number(input.actualSellTotal) / qty);
  } else {
    effective = suggested;
  }
  const coreCharge = Math.max(0, Number(input.coreCharge ?? 0));
  const taxable = input.taxable ?? true;

  const lineSubtotal = Number((qty * effective).toFixed(2));
  const gpDollars = Number((qty * (effective - cost)).toFixed(2));
  const gpPercent = effective > 0
    ? Number((((effective - cost) / effective) * 100).toFixed(2))
    : 0;
  const coreLine = Number((qty * coreCharge).toFixed(2));

  return {
    effectiveSell: Number(effective.toFixed(2)),
    suggestedSell: suggested,
    isUsingSuggested: !isManualOverride,
    lineSubtotal,
    gpDollars,
    gpPercent,
    coreLine,
    taxable,
  };
}

// =============================================================================
// PHASE 3.5 — Mode-aware actual-sell read (single source of truth)
// =============================================================================
/** Mode-aware authoritative read. Routes via pricing_method:
 *   - matrix_grouped → total is authority, each is display-derived.
 *   - everything else → each is authority, total is derived.
 *
 * DMC-I (2026-05-02): the legacy `unitSell` mirror parameter was retired
 * after the live audit + DMC-H closure. The Phase 3.5 enforce-authority
 * trigger (`parts_lines_enforce_authority`) guarantees `actual_sell_each`
 * is populated whenever the deprecated `unit_sell` mirror column is, so
 * `actualSellEach` is the SOLE per-unit read authority. The deprecated
 * `parts_lines.unit_sell` column itself is preserved (DMC-J/K scope).
 */
export interface ReadActualSellInput {
  quantity: number;
  pricingMethod?: string | null;
  actualSellEach?: number | null;
  actualSellTotal?: number | null;
}
export interface ReadActualSellResult {
  each: number | null;
  total: number | null;
  /** True when pricing_method is matrix_grouped (UI binds to total instead of each). */
  authorityIsTotal: boolean;
}
export function readActualSell(input: ReadActualSellInput): ReadActualSellResult {
  const qty = Math.max(0, Number(input.quantity || 0));
  const isGrouped = input.pricingMethod === 'matrix_grouped';

  if (isGrouped) {
    const total = input.actualSellTotal != null ? Number(input.actualSellTotal) : null;
    const each = total != null && qty > 0 ? round2(total / qty) : null;
    return { each, total, authorityIsTotal: true };
  }

  // DMC-I: per-unit authority is `actualSellEach` only. Pre-DMC-I this
  // branch fell back to the legacy `unitSell` mirror; the trigger
  // architecture now makes that mirror redundant for the read path.
  const each = input.actualSellEach != null ? Number(input.actualSellEach) : null;
  const total = each != null ? round2(each * qty) : null;
  return { each, total, authorityIsTotal: false };
}

export interface PartsRollupInput {
  lines: Array<{
    id?: string;
    job_id?: string | null;
    quantity: number;
    unit_cost: number | null;
    /** Phase 3.5 / DMC-H authority-contract fields. Required for correct sell-side rollup. */
    actual_sell_each: number | null;
    actual_sell_total: number | null;
    pricing_method: string | null;
    manual_price_locked: boolean;
    taxable: boolean;
    core_charge: number;
  }>;
  taxRules: { categories: TaxabilityCategories };
  taxSettings: TaxSettingsLike;
  feeRules?: FeeRule[];      // optional — only fees logically tied to parts will be included
  matrix?: PricingMatrixRow[];
}

export interface PartsRollupSection {
  partsSubtotal: number;
  coreSubtotal: number;
  taxableBase: number;
  estTax: number;
  estFees: number;
  estTotal: number;
}

export interface PartsRollupResult extends PartsRollupSection {
  perJob: Record<string, PartsRollupSection>;     // keyed by job_id; null/whole-ticket bucket = '__ticket__'
}

const TICKET_BUCKET = '__ticket__';

function rollupSection(
  lines: PartsRollupInput['lines'],
  taxRules: { categories: TaxabilityCategories },
  taxSettings: TaxSettingsLike,
  feeRules: FeeRule[],
  matrix: PricingMatrixRow[],
): PartsRollupSection {
  let partsSubtotal = 0;
  let coreSubtotal = 0;
  let taxableParts = 0;

  for (const l of lines) {
    const econ = computePartLineEconomics({
      qty: l.quantity,
      unitCost: l.unit_cost,
      actualSellEach: l.actual_sell_each,
      actualSellTotal: l.actual_sell_total,
      pricingMethod: l.pricing_method,
      manualPriceLocked: l.manual_price_locked,
      coreCharge: l.core_charge,
      taxable: l.taxable,
      matrix,
    });
    partsSubtotal += econ.lineSubtotal;
    coreSubtotal += econ.coreLine;
    if (l.taxable && taxRules.categories.parts) taxableParts += econ.lineSubtotal;
    if (l.taxable && taxRules.categories.core) taxableParts += econ.coreLine;
  }

  partsSubtotal = Number(partsSubtotal.toFixed(2));
  coreSubtotal = Number(coreSubtotal.toFixed(2));

  // Reuse Phase 1 calculator — feed parts subtotal as the "parts" contribution.
  // We pass labor=0 so only parts/fees affect the result.
  const phase1 = computeEstimate({
    partsSubtotal,
    laborSubtotal: 0,
    ticketSubtotal: partsSubtotal + coreSubtotal,
    feeRules,
    taxRules,
    taxSettings,
  });

  // taxableBase from Phase 1 already includes parts (when category enabled) + taxable fees.
  // Add core taxable contribution (Phase 1 doesn't see core lines directly).
  const taxableBase = Number((phase1.taxableBase + (taxRules.categories.core ? coreSubtotal : 0)).toFixed(2));
  const rate = taxSettings.combined_rate || 0;
  const estTax = Number((taxableBase * rate).toFixed(2));
  const estFees = phase1.feesTotal;
  const estTotal = Number((partsSubtotal + coreSubtotal + estFees + estTax).toFixed(2));

  return { partsSubtotal, coreSubtotal, taxableBase, estTax, estFees, estTotal };
}

/**
 * Roll up parts lines for a ticket — overall + grouped per job_id.
 * Reuses Phase 1 `computeEstimate` for tax/fee math; never duplicates it.
 */
export function rollupPartsForTicket(input: PartsRollupInput): PartsRollupResult {
  const matrix = input.matrix ?? FALLBACK_PRICING_MATRIX;
  const feeRules = input.feeRules ?? [];

  // Overall
  const overall = rollupSection(input.lines, input.taxRules, input.taxSettings, feeRules, matrix);

  // Per-job grouping
  const groups = new Map<string, PartsRollupInput['lines']>();
  for (const l of input.lines) {
    const key = l.job_id ?? TICKET_BUCKET;
    if (!groups.has(key)) groups.set(key, []);
    groups.get(key)!.push(l);
  }
  const perJob: Record<string, PartsRollupSection> = {};
  for (const [key, sectionLines] of groups.entries()) {
    perJob[key] = rollupSection(sectionLines, input.taxRules, input.taxSettings, feeRules, matrix);
  }

  return { ...overall, perJob };
}

// =============================================================================
// PHASE 3 — OptiGuard Hybrid Pricing Engine (suggestion-only, never authoritative)
// =============================================================================

export type PricingMode = 'flat' | 'matrix' | 'optiguard';

export interface ShopPricingCategoryRule {
  mode?: PricingMode;
  markupPct?: number;          // for 'flat'
  targetGp?: number;           // for 'optiguard'
  minMarkupPct?: number;       // category floor cap
  maxMarkupPct?: number;       // category ceiling cap
}

export interface ShopPricingConfig {
  defaultMode: PricingMode;
  globalMaxMultiplier: number;   // hard cap on multiplier
  globalMaxMarkupPct: number;    // hard cap on markup %
  categoryRules: Map<string, ShopPricingCategoryRule>;
  /** Matrices keyed by category; '__default__' = matrix used when no category-specific matrix. */
  matrixByCategory: Map<string, PricingMatrixRow[]>;
  /** Pre-computed OptiGuard-adjusted matrices, keyed by category. Suggestion only. */
  optiguardAdjustedMatrices?: Map<string, PricingMatrixRow[]>;
}

export const DEFAULT_MATRIX_KEY = '__default__';

export interface SuggestedSellResult {
  suggested: number;
  source: 'flat' | 'matrix' | 'optiguard' | 'fallback';
  clampedBy?: 'min' | 'max' | 'global';
}

function resolveMode(
  category: string | null,
  cfg: ShopPricingConfig,
): { mode: PricingMode; rule?: ShopPricingCategoryRule } {
  const rule = category ? cfg.categoryRules.get(category) : undefined;
  if (rule?.mode) return { mode: rule.mode, rule };
  return { mode: cfg.defaultMode, rule };
}

function lookupMatrix(cost: number, matrix: PricingMatrixRow[]): number {
  if (!matrix.length) return 0;
  for (const row of matrix) {
    if (cost <= row.maxCost) return cost * row.multiplier;
  }
  return cost * matrix[matrix.length - 1].multiplier;
}

function pickMatrix(
  category: string | null,
  cfg: ShopPricingConfig,
  preferOptiguard: boolean,
): { matrix: PricingMatrixRow[]; isOptiguard: boolean } {
  const key = category ?? DEFAULT_MATRIX_KEY;
  if (preferOptiguard && cfg.optiguardAdjustedMatrices?.has(key)) {
    return { matrix: cfg.optiguardAdjustedMatrices.get(key)!, isOptiguard: true };
  }
  if (preferOptiguard && cfg.optiguardAdjustedMatrices?.has(DEFAULT_MATRIX_KEY)) {
    return { matrix: cfg.optiguardAdjustedMatrices.get(DEFAULT_MATRIX_KEY)!, isOptiguard: true };
  }
  if (cfg.matrixByCategory.has(key)) {
    return { matrix: cfg.matrixByCategory.get(key)!, isOptiguard: false };
  }
  if (cfg.matrixByCategory.has(DEFAULT_MATRIX_KEY)) {
    return { matrix: cfg.matrixByCategory.get(DEFAULT_MATRIX_KEY)!, isOptiguard: false };
  }
  return { matrix: FALLBACK_PRICING_MATRIX, isOptiguard: false };
}

/**
 * Hybrid pricing suggestion — Phase 3 (with Phase 3.5 quantity-mode awareness).
 * Suggestion-only. Manual override (manual_price_locked) is the only authority for actual price.
 *
 * Backwards-compatible: callers that pass only (cost, category, cfg) get a per-unit suggestion.
 * Pass the optional 4th arg with `{ quantity, quantityMode }` to get full grouped-mode support.
 */
export interface SuggestedSellExtraOpts {
  quantity?: number;
  quantityMode?: 'per_unit' | 'grouped';
}
export interface SuggestedSellHybridResult extends SuggestedSellResult {
  /** Suggested per-unit (authoritative for per_unit mode, derived display for grouped). */
  suggestedEach: number;
  /** Suggested line total (authoritative for grouped mode, derived for per_unit). */
  suggestedTotal: number;
  method: 'matrix_per_unit' | 'matrix_grouped' | 'flat_markup' | 'optiguard' | 'fallback';
  tierSnapshot: { min: number | null; max: number | null; multiplier: number | null } | null;
  costBasis: number;
}

export function suggestedSellHybrid(
  cost: number,
  category: string | null,
  cfg: ShopPricingConfig,
  opts: SuggestedSellExtraOpts = {},
): SuggestedSellHybridResult {
  const qty = Math.max(0, Number(opts.quantity ?? 1));
  const mode = opts.quantityMode ?? 'per_unit';

  if (!cost || cost <= 0) {
    return {
      suggested: 0, source: 'fallback',
      suggestedEach: 0, suggestedTotal: 0,
      method: 'fallback', tierSnapshot: null, costBasis: 0,
    };
  }

  const { mode: priceMode, rule } = resolveMode(category, cfg);

  // Cost basis depends on quantity mode.
  // per_unit  → tier from cost_each, sell each = cost_each × multiplier
  // grouped   → tier from cost_each × qty, sell total = cost_total × multiplier
  const costBasis = mode === 'grouped' && qty > 0 ? round2(cost * qty) : cost;

  let raw = 0;
  let source: SuggestedSellResult['source'] = 'fallback';
  let method: SuggestedSellHybridResult['method'] = 'fallback';
  let tierSnapshot: SuggestedSellHybridResult['tierSnapshot'] = null;

  const captureTier = (m: PricingMatrixRow[]) => {
    const tier = findMatrixTier(costBasis, m);
    if (!tier) return null;
    return {
      min: null,        // PricingMatrixRow only carries maxCost; min is implicit. Snapshot below uses DB row.
      max: Number.isFinite(tier.maxCost) ? tier.maxCost : null,
      multiplier: tier.multiplier,
    };
  };

  if (priceMode === 'flat') {
    const markup = rule?.markupPct ?? 0;
    raw = costBasis * (1 + markup / 100);
    source = 'flat';
    method = 'flat_markup';
  } else if (priceMode === 'optiguard') {
    const picked = pickMatrix(category, cfg, true);
    raw = lookupMatrix(costBasis, picked.matrix);
    source = picked.isOptiguard ? 'optiguard' : 'matrix';
    method = picked.isOptiguard ? 'optiguard' : (mode === 'grouped' ? 'matrix_grouped' : 'matrix_per_unit');
    tierSnapshot = captureTier(picked.matrix);
  } else {
    const picked = pickMatrix(category, cfg, false);
    raw = lookupMatrix(costBasis, picked.matrix);
    source = cfg.matrixByCategory.size === 0 ? 'fallback' : 'matrix';
    method = mode === 'grouped' ? 'matrix_grouped' : 'matrix_per_unit';
    tierSnapshot = captureTier(picked.matrix);
  }

  // Clamps on the line authority value (raw).
  let clampedBy: SuggestedSellResult['clampedBy'];
  if (rule?.minMarkupPct != null) {
    const floor = costBasis * (1 + rule.minMarkupPct / 100);
    if (raw < floor) { raw = floor; clampedBy = 'min'; }
  }
  if (rule?.maxMarkupPct != null) {
    const ceiling = costBasis * (1 + rule.maxMarkupPct / 100);
    if (raw > ceiling) { raw = ceiling; clampedBy = 'max'; }
  }

  // Global caps. SYSTEM_MAX_MULTIPLIER is the absolute hard cap.
  const effectiveGlobalMult = Math.min(cfg.globalMaxMultiplier, SYSTEM_MAX_MULTIPLIER);
  const globalMultCap = costBasis * effectiveGlobalMult;
  if (raw > globalMultCap) { raw = globalMultCap; clampedBy = 'global'; }
  const globalMarkupCap = costBasis * (1 + cfg.globalMaxMarkupPct / 100);
  if (raw > globalMarkupCap) { raw = globalMarkupCap; clampedBy = 'global'; }

  // Mode-aware rounding contract:
  //  per_unit → round each first, then multiply for total.
  //  grouped  → round total first, then divide for display each.
  let suggestedEach: number;
  let suggestedTotal: number;
  if (mode === 'grouped') {
    suggestedTotal = round2(raw);
    suggestedEach = qty > 0 ? round2(suggestedTotal / qty) : 0;
  } else {
    suggestedEach = round2(raw);
    suggestedTotal = round2(suggestedEach * qty);
  }

  return {
    suggested: suggestedEach, // legacy field — per-unit
    source,
    clampedBy,
    suggestedEach,
    suggestedTotal,
    method,
    tierSnapshot,
    costBasis: round2(costBasis),
  };
}

/**
 * Build an empty/default ShopPricingConfig (uses fallback matrix only).
 */
export function emptyShopPricingConfig(): ShopPricingConfig {
  return {
    defaultMode: 'matrix',
    globalMaxMultiplier: 5.0,
    globalMaxMarkupPct: 400.0,
    categoryRules: new Map(),
    matrixByCategory: new Map([[DEFAULT_MATRIX_KEY, FALLBACK_PRICING_MATRIX]]),
  };
}

/**
 * OptiGuard adjustment — preview-only.
 * Returns a *new* matrix nudged toward `targetGp`, bounded by `maxStepPct`.
 * Returns null when sample count is below `minSamples` (outlier protection).
 *
 * Math: derive avg historical multiplier (1 / (1 - historicalGp/100)),
 * derive target multiplier (1 / (1 - targetGp/100)),
 * compute ratio capped at ±maxStepPct, apply to each row.
 */
export function computeOptiGuardMatrixAdjustment(
  historicalGp: number,
  targetGp: number,
  currentMatrix: PricingMatrixRow[],
  opts: { maxStepPct: number; minSamples: number; sampleCount: number },
): PricingMatrixRow[] | null {
  if (opts.sampleCount < opts.minSamples) return null;
  if (!currentMatrix.length) return null;
  const safeGp = (g: number) => Math.min(95, Math.max(-95, g));
  const histMult = 1 / (1 - safeGp(historicalGp) / 100);
  const targetMult = 1 / (1 - safeGp(targetGp) / 100);
  if (!isFinite(histMult) || histMult <= 0) return null;
  let ratio = targetMult / histMult;
  const maxStep = opts.maxStepPct / 100;
  ratio = Math.min(1 + maxStep, Math.max(1 - maxStep, ratio));
  return currentMatrix.map((row) => ({
    maxCost: row.maxCost,
    multiplier: Number((row.multiplier * ratio).toFixed(3)),
  }));
}


